/* eslint-disable import/no-extraneous-dependencies */

import { useEffect, useState, type CSSProperties } from 'react'

import SEO from '@cannect/components/SEO'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBrand } from 'hooks/useBrand'
import { useScheduling } from 'hooks/useScheduling'
import useSignedUser from 'hooks/useSignedUser'
import { useTokenApi } from 'hooks/useTokenApi'
import { CreditCardFormSchema, PixFormSchema } from 'pages/AppointmentScheduling/schedulingMock'
import * as Styles from 'pages/AppointmentScheduling/styles'
import { IAppointmentForm, ParamsProps } from 'pages/AppointmentScheduling/types'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import IdentificationStep from 'pages/AppointmentScheduling/FormSteps/CheckoutStep/IdentificationStep'
import 'styles/aspaec-theme.css'
import 'styles/remederi-theme.css'
import 'styles/vikura-theme.css'
import 'styles/branded-scheduling.css'
import schedulingBgImage from '@cannect/assets/img/scheduling/scheduling-hero-bg-desktop.webp'
import { clearBrandedIdentification } from 'utils/brandedIdentification'
import BrandedStepper from './BrandedStepper'
import ConsultationChoiceStep from './FormSteps/ConsultationChoiceStep'
import FilterByStepBranded from './FormSteps/FilterByStep'
import SchedulingTriageStep from './FormSteps/TriageStep'

function AppointmentSchedulingBranded() {
  const {
    step,
    currentStep,
    getScheduling,
    appointmentFromB2C,
    selectedPaymentMethod,
    checkoutActiveStep,
    setCheckoutActiveStep
  } = useScheduling()
  const { config } = useBrand()
  const { cart_id } = useParams<ParamsProps>()
  const { signedUser } = useSignedUser()
  const { tokenApi } = useTokenApi()

  const createAppointmentForm = useForm<IAppointmentForm>({
    mode: 'onChange',
    shouldUnregister: false,
    resolver: selectedPaymentMethod === 'pix' ? zodResolver(PixFormSchema) : zodResolver(CreditCardFormSchema),
    defaultValues: {
      filter: '',
      search_date: null,
      scheduling_date: null,
      scheduling_time: '',
      coupon: '',
      amountWithDiscount: undefined,
      creditAmountWithDiscount: undefined,
      agreeWithTerms: false,
      patient: null,
      prescriber: null,
      address: {
        cep: '',
        city: '',
        address: '',
        number: '',
        state: ''
      },
      payment: {
        card_holder_name: '',
        card_validate: '',
        card_number: '',
        card_cvv: ''
      }
    }
  })

  const { setValue, watch } = createAppointmentForm
  const identifiedPatient = watch('patient')

  // Fluxo branded (Remederi/Aspaec) é "dados primeiro": o paciente SEMPRE
  // informa os dados ao entrar, mesmo com sessão ativa no navegador — o
  // register-or-retrieve resolve se o user existe e devolve o token. O cadastro
  // com origin da marca já cria o negócio no funil (Lead) no backend.
  // cart_id = retomada de agendamento existente — não passa pelo portão.
  // needsIdentification pode ser forçado de volta a true pelo botão "voltar"
  // da Triagem, limpando o patient já identificado.
  const needsIdentification = !identifiedPatient && !cart_id
  const goBackToIdentification = () => setValue('patient', null)

  // Nova passagem pela tela de dados invalida a identificação anterior
  // reaproveitada pela Consulta na Hora (regra "dados primeiro").
  useEffect(() => {
    if (needsIdentification) clearBrandedIdentification()
  }, [needsIdentification])

  // Marcas com Consulta na Hora (ex. Vikura): após a identificação o paciente
  // escolhe entre consulta agendada e Consulta na Hora em dois cards, e só
  // então segue para a triagem específica. Retomada por cart_id pula a escolha.
  const hasConsultationChoice = config.scheduling.hasInstantConsultation === true
  const [consultationChoiceDone, setConsultationChoiceDone] = useState(false)
  const needsConsultationChoice = hasConsultationChoice && !needsIdentification && !consultationChoiceDone && !cart_id

  // Triagem clínica (passo 3 da jornada Remederi): após os dados do paciente,
  // antes da escolha do médico. Retomada por cart_id pula a triagem.
  const [triageDone, setTriageDone] = useState(false)
  const triagePatientUserId = identifiedPatient?.id ?? signedUser?.id ?? null
  const needsTriage =
    config.scheduling.hasTriage === true &&
    !needsIdentification &&
    !needsConsultationChoice &&
    !triageDone &&
    !cart_id &&
    Boolean(triagePatientUserId)

  useEffect(() => {
    if (cart_id) {
      const uuid = cart_id.split('=')[1]
      getScheduling(uuid)
    }
  }, [cart_id])

  // Fluxo branded entra no checkout SEMPRE pela Revisão (passo 2): com token de
  // sessão salvo, o useScheduling inicia o checkout em 2 e o cadastro chama
  // nextCheckoutStep → 3, pulando a Revisão — que é quem CRIA o agendamento.
  // Sem isso, a tela de pagamento abria sem agendamento ("Agendamento não
  // encontrado" / nenhuma API chamada). cart_id (link de pagamento) não passa
  // aqui: o getScheduling posiciona no passo 3 com o agendamento carregado.
  useEffect(() => {
    if (!cart_id && currentStep !== 0) {
      setCheckoutActiveStep(2)
    }
  }, [cart_id, currentStep])

  useEffect(() => {
    if (appointmentFromB2C) {
      setValue('scheduling_date', appointmentFromB2C?.date)
      setValue('scheduling_time', appointmentFromB2C?.hour)
      setValue('amountWithDiscount', parseFloat(appointmentFromB2C?.amount_with_discount))
      setValue('creditAmountWithDiscount', parseFloat(appointmentFromB2C?.credit_amount_with_discount))
      if (appointmentFromB2C?.coupon?.name) setValue('coupon', appointmentFromB2C?.coupon?.name || '')
    }
  }, [appointmentFromB2C])

  useEffect(() => {
    // Só na retomada por cart_id a sessão preenche o paciente automaticamente;
    // fluxo novo sempre passa pela tela de dados (regra whitelabel).
    if (cart_id && tokenApi && signedUser) {
      setValue('patient', signedUser)
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    document?.getElementById('page-wrapper')?.scrollTo(0, 0)
    document?.getElementById('main-app-container')?.scrollTo(0, 0)
  }, [currentStep, checkoutActiveStep])

  const themeClass = config.scheduling.themeClass ?? ''

  // Imagem hero só na 1a etapa (identificação/dados do paciente). Nas telas
  // de conteúdo alto com scroll (escolha de médico, checkout) a imagem daria
  // super-zoom — usa cor sólida da marca (mesmo padrão do agendamento Cannect).
  const showHeroImage = currentStep === 0 && needsIdentification

  // Stepper único da jornada branded (o interno do CheckoutStep fica oculto
  // nas marcas). Passo ativo derivado do estado da página + checkout.
  // Primeiro passo sem label (""): o heading "Dados do paciente" do
  // IdentificationStep já identifica a etapa, o "Dados" do stepper era redundante.
  const hasTriageStep = config.scheduling.hasTriage === true
  const stepperLabels = hasTriageStep
    ? ['', 'Triagem', 'Médico e horário', 'Revisão', 'Pagamento', 'Conclusão']
    : ['', 'Médico e horário', 'Revisão', 'Pagamento', 'Conclusão']
  const reviewStepIndex = hasTriageStep ? 3 : 2
  let activeStepIndex: number
  if (currentStep === 0) {
    if (needsIdentification) activeStepIndex = 0
    // A escolha agendada/na hora fica entre os dados e a triagem — marca o
    // passo "Triagem" como ativo, já que os cards levam à triagem específica.
    else if (needsConsultationChoice || needsTriage) activeStepIndex = 1
    else activeStepIndex = hasTriageStep ? 2 : 1
  } else {
    // checkoutActiveStep: 2=Revisão, 3=Pagamento, 4=Conclusão
    activeStepIndex = Math.min(reviewStepIndex + Math.max(checkoutActiveStep - 2, 0), stepperLabels.length - 1)
  }

  return (
    <>
      <SEO
        title={`Agendamento de Consulta ${config.name}`}
        description={`Agende sua consulta com um especialista parceiro ${config.name}.`}
        canonical={config.scheduling.path ?? '/agendamento'}
      />

      <div
        className={`branded-scheduling-bg ${showHeroImage ? 'has-hero' : ''} ${themeClass}`.trim()}
        style={{ '--scheduling-bg-image': `url(${schedulingBgImage})` } as CSSProperties}>
        <Styles.Container className={`${themeClass} ${currentStep !== 0 ? 'mx-auto max-w-[1366px]' : ''}`.trim()}>
          <BrandedStepper steps={stepperLabels} activeIndex={activeStepIndex} />
          <Styles.TabContainer>
            <FormProvider {...createAppointmentForm}>
              {currentStep === 0 && needsIdentification && <IdentificationStep />}
              {currentStep === 0 && !needsIdentification && needsConsultationChoice && (
                <ConsultationChoiceStep
                  onChooseScheduled={() => setConsultationChoiceDone(true)}
                  onBack={goBackToIdentification}
                />
              )}
              {currentStep === 0 && !needsIdentification && !needsConsultationChoice && needsTriage && (
                <SchedulingTriageStep
                  patientUserId={triagePatientUserId as number}
                  onComplete={() => setTriageDone(true)}
                  onBack={hasConsultationChoice ? () => setConsultationChoiceDone(false) : goBackToIdentification}
                />
              )}
              {currentStep === 0 && !needsIdentification && !needsConsultationChoice && !needsTriage && (
                <FilterByStepBranded onBack={hasTriageStep ? () => setTriageDone(false) : goBackToIdentification} />
              )}
              {currentStep !== 0 && step}
            </FormProvider>
          </Styles.TabContainer>
        </Styles.Container>
      </div>
    </>
  )
}

export default AppointmentSchedulingBranded
